import {MutableRefObject, useMemo} from 'react';

import {any, AnyZodObject, object, RefinementCtx, string, ZodIssueCode} from 'zod';

import {ApplicationStoreType} from 'desktop/store';
import {AddressFormType, CurrentAddressType} from 'desktop/types/addressType';
import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';
import {useSelectorRef} from 'hooks/useSelectorRef/useSelectorRef';
import {RegionsItemType} from 'hostApplicationStoreType';

export const emailValidationSchema = string()
    .min(1, 'Please enter your details')
    .email('This email address is invalid')
    .refine(value => !/[а-яё]/gim.test(value), 'This email address is invalid');

const selectableStateSchema = string()
    .trim()
    .min(1, 'Please enter your details')
    .refine(value => value !== '0', 'Please enter your details');

const baseAddressSchema = object({
    address: string()
        .min(1, 'Please enter your details')
        .min(3, 'We can׳t find this address, please enter a valid one'),
    city: string().trim().min(1, 'Please enter your details'),
    country: string().trim().min(1, 'Please enter your details'),
    email: string().optional(),
    firstName: string()
        .trim()
        .min(1, 'Please enter your details')
        .regex(/^[a-z][a-z,'.\-\s]+$/gi, 'This first name is invalid'),
    lastName: string()
        .trim()
        .min(1, 'Please enter your details')
        .regex(/^[a-z][a-z,'.\-\s]+$/gi, 'This last name is invalid'),
    other: string().optional(),
    phone: string()
        .min(1, 'Please enter your details')
        .min(2, 'Please enter at least 2 characters')
        .regex(/[\d+()-]/gim, 'This number is invalid'),
    postal: string().trim().min(1, 'Please enter your details'),
    regionID: any().optional(),
    state: string(),
});

export const baseSchema = object({
    billingAddress: baseAddressSchema,
    shippingAddress: baseAddressSchema,
});

const schemaForGuest = baseSchema.extend({
    billingAddress: baseAddressSchema.extend({
        email: emailValidationSchema,
    }),
    shippingAddress: baseAddressSchema.extend({
        email: emailValidationSchema,
    }),
});

const validatePostal = ({
    ctx,
    isShipping,
    values,
}: {
    values: AddressFormType;
    ctx: RefinementCtx;
    isShipping: boolean;
}) => {
    const addressValues = isShipping ? values.shippingAddress : values.billingAddress;
    const regexForPostal = addressValues.country === 'US' ? /^\d{5}([-\s]\d{4})?$/ : /[\w\s\d]/gim;
    if (!regexForPostal.test(addressValues.postal.trim())) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            message: 'This code is invalid',
            path: [isShipping ? 'shippingAddress' : 'billingAddress', 'postal'],
        });
    }
};

/**
 * Generate zod schema for address form on checkout page, new and edit address pages
 * onlyForType use only in new and edit address pages, it omits shippingAddress or billingAddress
 * also applies super refine to validate states and postal code
 */
const generateSchema = (
    schema: AnyZodObject,
    statesRef: MutableRefObject<Record<string, RegionsItemType[]>>,
    onlyForType?: CurrentAddressType,
) => {
    const validateState = ({
        ctx,
        isShipping,
        values,
    }: {
        values: AddressFormType;
        ctx: RefinementCtx;
        isShipping: boolean;
    }) => {
        const addressValues = isShipping ? values.shippingAddress : values.billingAddress;
        const isStateSelectable = statesRef.current[addressValues.country]
            ? statesRef.current[addressValues.country].length > 0
            : false;
        if (isStateSelectable) {
            const {error} = selectableStateSchema.safeParse(addressValues.state);
            if (error)
                ctx.addIssue({
                    ...error.issues[0],
                    path: [isShipping ? 'shippingAddress' : 'billingAddress', 'state'],
                });
        }
    };

    let finalSchema = schema;

    if (onlyForType === 'billing') finalSchema = schema.omit({shippingAddress: true});
    else if (onlyForType === 'shipping') finalSchema = schema.omit({billingAddress: true});

    return finalSchema.superRefine((values, ctx) => {
        if (!onlyForType || onlyForType === 'shipping') {
            validateState({ctx, isShipping: true, values: values as AddressFormType});
            validatePostal({ctx, isShipping: true, values: values as AddressFormType});
        }

        if (!onlyForType || onlyForType === 'billing') {
            validateState({ctx, isShipping: false, values: values as AddressFormType});
            validatePostal({ctx, isShipping: false, values: values as AddressFormType});
        }
    });
};

/**
 * Return zod schema based on logged in status and edit form type
 * when editing shipping address onlyForType is equal to 'shipping'
 * when editing billing address onlyForType is equal to 'billing'
 */
export const useDeliveryFormValidationSchema = ({
    onlyForType,
}: {onlyForType?: CurrentAddressType} = {}) => {
    const customer = useGetCustomerData();
    const statesRef = useSelectorRef((state: ApplicationStoreType) => state.location.states);

    return useMemo(
        () =>
            generateSchema(
                customer?.is_logged_in ? baseSchema : schemaForGuest,
                statesRef,
                onlyForType,
            ),
        [customer?.is_logged_in, statesRef, onlyForType],
    );
};
